import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import MemoriesGuestOrUserRoute from './MemoriesGuestOrUserRoute';
import MemoriesList from '../pages/MemoriesList';
import MemoriesCreate from '../pages/MemoriesCreate';
import MemoriesEvent from '../pages/MemoriesEvent';
import MemoriesUpload from '../pages/MemoriesUpload';
import MemoriesJoin from '../pages/MemoriesJoin';
import MemoriesShare from '../pages/MemoriesShare';
import MemoriesAuthHandoff from '../pages/MemoriesAuthHandoff';
import MemoriesLogoutClear from '../pages/MemoriesLogoutClear';
import {
  memoriesAuthEntryUrls,
  isMemoriesLogoutInProgress,
  memoriesCrossLogoutUrl,
  sanitizeMemoriesReturnUrl,
} from '../utils/memoriesAuthBridge';
import { mainAppOrigin } from '../utils/memoriesHost';

function RedirectJoin() {
  const { code } = useParams();
  return <Navigate to={`/j/${code}`} replace />;
}

function RedirectShare() {
  const { code } = useParams();
  return <Navigate to={`/v/${code}`} replace />;
}

function RedirectEvent({ suffix = '' }) {
  const { slug } = useParams();
  return <Navigate to={`/e/${slug}${suffix}`} replace />;
}

function RedirectExternal({ to }) {
  useEffect(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    window.location.replace(`${to}${search || ''}${hash || ''}`);
  }, [to]);
  return null;
}

/** Prefer explicit return, then prior Memories page, then Memories home. */
function resolveMemoriesLoginReturn(searchParams, locationState) {
  const fromQuery = searchParams.get('return') || searchParams.get('next');
  if (fromQuery) {
    if (/^https?:\/\//i.test(fromQuery)) {
      return sanitizeMemoriesReturnUrl(fromQuery);
    }
    if (fromQuery.startsWith('/') && typeof window !== 'undefined') {
      return sanitizeMemoriesReturnUrl(`${window.location.origin}${fromQuery}`);
    }
  }
  const fromState = locationState?.from;
  if (fromState?.pathname && typeof window !== 'undefined') {
    return sanitizeMemoriesReturnUrl(
      `${window.location.origin}${fromState.pathname}${fromState.search || ''}`
    );
  }
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      const ref = new URL(document.referrer);
      if (ref.origin === window.location.origin) {
        return sanitizeMemoriesReturnUrl(ref.href);
      }
    } catch {
      // ignore bad referrer
    }
  }
  return sanitizeMemoriesReturnUrl(
    typeof window !== 'undefined' ? `${window.location.origin}/` : '/'
  );
}

/** Traveler auth lives on the main app — one account for Déjà Vu Nomad + Memories. */
function RedirectToMainAuth({ mode = 'login' }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    if (isMemoriesLogoutInProgress()) {
      window.location.replace(memoriesCrossLogoutUrl(`${mainAppOrigin()}/login`));
      return;
    }
    const returnUrl = resolveMemoriesLoginReturn(searchParams, location.state);
    const urls = memoriesAuthEntryUrls(returnUrl);
    window.location.replace(mode === 'register' ? urls.register : urls.login);
  }, [mode, searchParams, location.state]);
  return null;
}

/** Routes allowed on memories.dejavunomad.com — Memories + shared traveler auth. */
const MemoriesHostRoutes = () => (
  <Routes>
    <Route path="/auth/handoff" element={<MemoriesAuthHandoff />} />
    <Route path="/auth/logout-clear" element={<MemoriesLogoutClear />} />
    <Route path="/login" element={<RedirectToMainAuth mode="login" />} />
    <Route path="/register" element={<RedirectToMainAuth mode="register" />} />
    <Route path="/forgot-password" element={<RedirectExternal to={`${mainAppOrigin()}/forgot-password`} />} />
    <Route path="/reset-password" element={<RedirectExternal to={`${mainAppOrigin()}/reset-password`} />} />
    <Route path="/verify-email" element={<RedirectExternal to={`${mainAppOrigin()}/verify-email`} />} />
    <Route path="/" element={<MemoriesList />} />
    <Route path="/new" element={<PrivateRoute><MemoriesCreate /></PrivateRoute>} />
    <Route path="/j/:code" element={<MemoriesJoin />} />
    <Route path="/v/:code" element={<MemoriesShare />} />
    <Route path="/e/:slug/edit" element={<PrivateRoute><MemoriesCreate /></PrivateRoute>} />
    <Route path="/e/:slug/add" element={<MemoriesUpload />} />
    <Route path="/e/:slug" element={<MemoriesGuestOrUserRoute><MemoriesEvent /></MemoriesGuestOrUserRoute>} />
    <Route path="/memories" element={<Navigate to="/" replace />} />
    <Route path="/memories/new" element={<Navigate to="/new" replace />} />
    <Route path="/memories/j/:code" element={<RedirectJoin />} />
    <Route path="/memories/v/:code" element={<RedirectShare />} />
    <Route path="/memories/e/:slug/edit" element={<RedirectEvent suffix="/edit" />} />
    <Route path="/memories/e/:slug/add" element={<RedirectEvent suffix="/add" />} />
    <Route path="/memories/e/:slug" element={<RedirectEvent />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default MemoriesHostRoutes;
